import { useEffect, useState } from "react";
import { Table, Row, Col, Card, ButtonGroup, Button, Form } from "react-bootstrap";

import {
    getAdminDownloadStats,
    getTopDownloadedScripts,
    getDownloadsByDay,
    getTopDownloadUsers,
    getNewestUsersActivity,
} from "../utils/Database";
import { LoadingRing } from "../components/LoadingRing";

type DownloadStats = {
    total_all_time: number;
    total_today: number;
    total_this_week: number;
    total_this_month: number;
    total_this_year: number;
    unique_scripts_downloaded: number;
    unique_users_downloading: number;
};

type TopScript = {
    script_id: string;
    script_title: string;
    writers: string;
    download_count: number;
};

type DayDownload = {
    day: string;
    download_count: number;
};

type TopUser = {
    user_id: string;
    email: string;
    download_count: number;
    unique_scripts: number;
};

type NewAccount = {
    user_id: string;
    email: string;
    email_confirmed_at: string | null;
    last_sign_in_at: string | null;
    auth_created_at: string;
    userdata_created_at: string | null;
    banned_until: string | null;
    download_count: number;
    unique_scripts: number;
};

const DAY_RANGE_OPTIONS: { label: string; value: number | null }[] = [
    { label: "7d", value: 7 },
    { label: "30d", value: 30 },
    { label: "90d", value: 90 },
    { label: "All time", value: null },
];

const CHART_DAY_OPTIONS = [7, 30, 90];
const LIMIT_OPTIONS = [10, 25, 50, 100];

function StatCard({ label, value }: { label: string; value: number | null }) {
    return (
        <Card className="text-center h-100">
            <Card.Body>
                <div className="fs-2 fw-bold">{value != null ? value.toLocaleString() : "—"}</div>
                <div className="text-muted small">{label}</div>
            </Card.Body>
        </Card>
    );
}

function TableControls({
    limit,
    onLimitChange,
    daysBack,
    onDaysBackChange,
    disabled,
}: {
    limit: number;
    onLimitChange: (n: number) => void;
    daysBack: number | null;
    onDaysBackChange: (n: number | null) => void;
    disabled: boolean;
}) {
    return (
        <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
            <ButtonGroup size="sm">
                {DAY_RANGE_OPTIONS.map((opt) => (
                    <Button
                        key={String(opt.value)}
                        variant={daysBack === opt.value ? "primary" : "outline-secondary"}
                        onClick={() => onDaysBackChange(opt.value)}
                        disabled={disabled}
                    >
                        {opt.label}
                    </Button>
                ))}
            </ButtonGroup>
            <Form.Select
                size="sm"
                value={limit}
                onChange={(e) => onLimitChange(Number(e.target.value))}
                disabled={disabled}
                style={{ width: "auto" }}
            >
                {LIMIT_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                        Show {n}
                    </option>
                ))}
            </Form.Select>
        </div>
    );
}

function DownloadBarChart({ data }: { data: DayDownload[] }) {
    const max = Math.max(...data.map((d) => d.download_count), 1);

    return (
        <div className="download-bar-chart">
            {data.map((d) => {
                const pct = Math.round((d.download_count / max) * 100);
                const label = new Date(d.day + "T00:00:00").toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                });
                return (
                    <div key={d.day} className="bar-row d-flex align-items-center gap-2 mb-1">
                        <div className="bar-label text-muted small text-end" style={{ minWidth: "56px" }}>
                            {label}
                        </div>
                        <div className="flex-grow-1 bg-light rounded" style={{ height: "18px" }}>
                            <div
                                className="bg-primary rounded"
                                style={{ width: `${pct}%`, height: "100%", minWidth: d.download_count > 0 ? "4px" : "0" }}
                            />
                        </div>
                        <div className="small" style={{ minWidth: "28px" }}>
                            {d.download_count}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default function AdminStats() {
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState<DownloadStats | null>(null);

    const [topScripts, setTopScripts] = useState<TopScript[]>([]);
    const [scriptsLimit, setScriptsLimit] = useState(10);
    const [scriptsDaysBack, setScriptsDaysBack] = useState<number | null>(null);
    const [scriptsLoading, setScriptsLoading] = useState(false);

    const [topUsers, setTopUsers] = useState<TopUser[]>([]);
    const [usersLimit, setUsersLimit] = useState(10);
    const [usersDaysBack, setUsersDaysBack] = useState<number | null>(null);
    const [usersLoading, setUsersLoading] = useState(false);

    const [chartDayRange, setChartDayRange] = useState(30);
    const [downloadsByDay, setDownloadsByDay] = useState<DayDownload[]>([]);
    const [chartLoading, setChartLoading] = useState(false);

    const [newestAccounts, setNewestAccounts] = useState<NewAccount[]>([]);
    const [newestLimit, setNewestLimit] = useState(10);
    const [newestLoading, setNewestLoading] = useState(false);

    useEffect(() => {
        async function loadAll() {
            setLoading(true);
            const [statsData, scriptsData, usersData, dayData, newestData] = await Promise.all([
                getAdminDownloadStats(),
                getTopDownloadedScripts(scriptsLimit, scriptsDaysBack),
                getTopDownloadUsers(usersLimit, usersDaysBack),
                getDownloadsByDay(chartDayRange),
                getNewestUsersActivity(newestLimit),
            ]);
            setStats(statsData);
            setTopScripts(scriptsData);
            setTopUsers(usersData);
            setDownloadsByDay(dayData);
            setNewestAccounts(newestData);
            setLoading(false);
        }
        loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function refetchScripts(limit: number, daysBack: number | null) {
        setScriptsLoading(true);
        const data = await getTopDownloadedScripts(limit, daysBack);
        setTopScripts(data);
        setScriptsLoading(false);
    }

    async function refetchUsers(limit: number, daysBack: number | null) {
        setUsersLoading(true);
        const data = await getTopDownloadUsers(limit, daysBack);
        setTopUsers(data);
        setUsersLoading(false);
    }

    function handleScriptsLimit(limit: number) {
        setScriptsLimit(limit);
        refetchScripts(limit, scriptsDaysBack);
    }

    function handleScriptsDaysBack(daysBack: number | null) {
        setScriptsDaysBack(daysBack);
        refetchScripts(scriptsLimit, daysBack);
    }

    function handleUsersLimit(limit: number) {
        setUsersLimit(limit);
        refetchUsers(limit, usersDaysBack);
    }

    function handleUsersDaysBack(daysBack: number | null) {
        setUsersDaysBack(daysBack);
        refetchUsers(usersLimit, daysBack);
    }

    async function refetchNewest(limit: number) {
        setNewestLoading(true);
        const data = await getNewestUsersActivity(limit);
        setNewestAccounts(data);
        setNewestLoading(false);
    }

    function handleNewestLimit(limit: number) {
        setNewestLimit(limit);
        refetchNewest(limit);
    }

    async function changeChartDayRange(days: number) {
        setChartDayRange(days);
        setChartLoading(true);
        const dayData = await getDownloadsByDay(days);
        setDownloadsByDay(dayData);
        setChartLoading(false);
    }

    if (loading) {
        return <LoadingRing />;
    }

    return (
        <>
            <h3>Download Statistics</h3>

            <Row xs={2} md={4} className="g-3 mb-4">
                <Col><StatCard label="All Time" value={stats?.total_all_time ?? null} /></Col>
                <Col><StatCard label="Today" value={stats?.total_today ?? null} /></Col>
                <Col><StatCard label="This Week" value={stats?.total_this_week ?? null} /></Col>
                <Col><StatCard label="This Month" value={stats?.total_this_month ?? null} /></Col>
                <Col><StatCard label="This Year" value={stats?.total_this_year ?? null} /></Col>
                <Col><StatCard label="Unique Scripts Downloaded" value={stats?.unique_scripts_downloaded ?? null} /></Col>
                <Col><StatCard label="Unique Downloading Users" value={stats?.unique_users_downloading ?? null} /></Col>
            </Row>

            <Row className="g-4 mb-4">
                <Col xs={12} xl={6}>
                    <h4 className="mb-2">Top Downloaded Scripts</h4>
                    <TableControls
                        limit={scriptsLimit}
                        onLimitChange={handleScriptsLimit}
                        daysBack={scriptsDaysBack}
                        onDaysBackChange={handleScriptsDaysBack}
                        disabled={scriptsLoading}
                    />
                    {scriptsLoading ? (
                        <LoadingRing />
                    ) : topScripts.length > 0 ? (
                        <Table striped hover size="sm">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Title</th>
                                    <th>Writer(s)</th>
                                    <th>Downloads</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topScripts.map((s, i) => (
                                    <tr key={s.script_id}>
                                        <td>{i + 1}</td>
                                        <td>{s.script_title || "—"}</td>
                                        <td>{s.writers || "—"}</td>
                                        <td>{s.download_count.toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : (
                        <p className="text-muted">No data available.</p>
                    )}
                </Col>

                <Col xs={12} xl={6}>
                    <h4 className="mb-2">Most Active Downloaders</h4>
                    <TableControls
                        limit={usersLimit}
                        onLimitChange={handleUsersLimit}
                        daysBack={usersDaysBack}
                        onDaysBackChange={handleUsersDaysBack}
                        disabled={usersLoading}
                    />
                    {usersLoading ? (
                        <LoadingRing />
                    ) : topUsers.length > 0 ? (
                        <Table striped hover size="sm">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Email</th>
                                    <th>Downloads</th>
                                    <th>Unique Scripts</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topUsers.map((u, i) => (
                                    <tr key={u.user_id}>
                                        <td>{i + 1}</td>
                                        <td>{u.email}</td>
                                        <td>{u.download_count.toLocaleString()}</td>
                                        <td>{u.unique_scripts.toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : (
                        <p className="text-muted">No data available.</p>
                    )}
                </Col>
            </Row>

            <div className="d-flex align-items-center gap-3 mb-2">
                <h4 className="mb-0">Downloads Over Time</h4>
                <ButtonGroup size="sm">
                    {CHART_DAY_OPTIONS.map((d) => (
                        <Button
                            key={d}
                            variant={chartDayRange === d ? "primary" : "outline-secondary"}
                            onClick={() => changeChartDayRange(d)}
                            disabled={chartLoading}
                        >
                            {d}d
                        </Button>
                    ))}
                </ButtonGroup>
            </div>

            {chartLoading ? (
                <LoadingRing />
            ) : downloadsByDay.length > 0 ? (
                <div className="mb-4" style={{ maxWidth: "700px" }}>
                    <DownloadBarChart data={downloadsByDay} />
                </div>
            ) : (
                <p className="text-muted mb-4">No download data available.</p>
            )}

            <div className="d-flex align-items-center gap-3 mb-2">
                <h4 className="mb-0">Newest Accounts</h4>
                <Form.Select
                    size="sm"
                    value={newestLimit}
                    onChange={(e) => handleNewestLimit(Number(e.target.value))}
                    disabled={newestLoading}
                    style={{ width: "auto" }}
                >
                    {LIMIT_OPTIONS.map((n) => (
                        <option key={n} value={n}>Show {n}</option>
                    ))}
                </Form.Select>
            </div>
            {newestLoading ? (
                <LoadingRing />
            ) : newestAccounts.length > 0 ? (
                <div className="table-responsive mb-4">
                    <Table striped hover size="sm">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Email</th>
                                <th>Status</th>
                                <th>Registered</th>
                                <th>Last Login</th>
                                <th>Downloads</th>
                                <th>Unique Scripts</th>
                            </tr>
                        </thead>
                        <tbody>
                            {newestAccounts.map((u, i) => {
                                const status = u.banned_until !== null ? "Banned"
                                    : u.userdata_created_at ? "Active"
                                    : u.email_confirmed_at ? "Confirmed"
                                    : "Registered";
                                return (
                                    <tr key={u.user_id}>
                                        <td>{i + 1}</td>
                                        <td>{u.email}</td>
                                        <td>{status}</td>
                                        <td>{new Date(u.auth_created_at).toLocaleDateString()}</td>
                                        <td>{u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleDateString() : "—"}</td>
                                        <td>{u.download_count.toLocaleString()}</td>
                                        <td>{u.unique_scripts.toLocaleString()}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            ) : (
                <p className="text-muted mb-4">No account data available.</p>
            )}
        </>
    );
}
